import { Box, ButtonBase, Checkbox, Popper } from '@mui/material'
import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { flushSync } from 'react-dom'
import {
  useGridApiContext,
  GridColumnMenuItemProps,
  gridColumnMenuSelector,
  gridColumnVisibilityModelSelector,
} from '@mui/x-data-grid-premium'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useLocation } from 'react-router-dom'
import SearchBar from '@/components/common/SearchBar'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  getColumnStorageKey,
  getColumnSettings,
  saveColumnSettings,
} from '@/utils/columnStorage'

// Fields that cannot be hidden or reordered
const LOCKED_FIELDS = new Set(['meterNumber'])

const BULK_VISIBILITY_ACTIONS = [
  { label: 'בחר הכל', visible: true },
  { label: 'נקה הכל', visible: false },
]

// Lift the organizer through Popper, not a CSS margin. Popper measures only
// its own element, so a margin hides the top 100px from its overflow checks
// and a tall organizer can flip and open above the top of the screen.
// Popper keeps a side placement on screen only along its height. On a phone
// neither side of the column menu has room, so let it slide across as well.
const POPPER_MODIFIERS = [
  { name: 'offset', options: { offset: [-100, 0] } },
  {
    name: 'preventOverflow',
    options: { altAxis: true, padding: { left: 8, right: 8 } },
  },
]

// When MUI X 8.10 remounts the header that holds this column menu, the new
// header draws no menu: it passes its menu button as the menu's anchor
// before that button exists, and nothing renders the header again while
// the menu stays open. Showing a column before the menu's column causes
// such a remount, because the grid draws the headers once with the column
// range from before the change. The organizer then closes and reopens the
// column menu in two renders. The new organizer takes what the old one
// showed from here, per grid.
interface KeptOrganizer {
  field: string
  search: string
}
const keptOrganizers = new WeakMap<object, KeptOrganizer>()

// About eight single-line rows: a 42px checkbox, 12px padding and a 4px gap.
const LIST_MAX_HEIGHT = 8 * 58

interface OrganizerColumn {
  field: string
  headerName: string
  hide: boolean
}

interface SortableItemProps {
  id: string
  col: OrganizerColumn
  toggleColumn: (field: string) => void
  colors: Record<string, string>
}

function SortableItem({ id, col, toggleColumn, colors }: SortableItemProps) {
  const isLocked = LOCKED_FIELDS.has(col.field)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isLocked })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    mb: 4,
    borderRadius: 4,
    cursor: isLocked ? 'default' : 'grab',
    width: '200px',
    boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.15)' : 'none',
    backgroundColor: colors.itemBg,
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={e => {
        e.stopPropagation()
      }}
      sx={{
        padding: '6px 8px',
        marginBottom: '4px',
        borderRadius: '4px',
        opacity: isLocked ? 0.7 : 1,
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: isLocked ? 'transparent' : colors.hoverBg,
        },
      }}
    >
      <span style={{ color: colors.text, fontSize: '14px', fontWeight: 300 }}>
        {col.headerName}
      </span>
      <Checkbox
        sx={{
          color: colors.checkboxBorder,
          '&.Mui-checked': {
            color: '#81B5FF',
          },
        }}
        size="medium"
        checked={!col.hide}
        disabled={isLocked}
        onChange={() => toggleColumn(col.field)}
      />
      <Box
        {...attributes}
        {...listeners}
        sx={{
          color: isLocked ? colors.checkboxBorder : colors.dragIcon,
          pointerEvents: isLocked ? 'none' : 'auto',
        }}
      >
        <DragIndicatorIcon />
      </Box>
    </Box>
  )
}

export default function CustomDraggableManageColumnsMenu({
  colDef,
}: GridColumnMenuItemProps) {
  const apiRef = useGridApiContext()
  const grid = apiRef.current
  const [kept] = useState(() => {
    const organizer = keptOrganizers.get(grid)
    return organizer?.field === colDef.field ? organizer : undefined
  })
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [columns, setColumns] = useState<OrganizerColumn[]>([])
  const [searchValue, setSearchValue] = useState(kept?.search ?? '')
  const shown = useRef({ open, search: searchValue })
  shown.current = { open, search: searchValue }

  useEffect(() => {
    keptOrganizers.delete(grid)
    return () => {
      const menu = gridColumnMenuSelector(apiRef)
      if (!shown.current.open || !menu.open || menu.field !== colDef.field)
        return
      const organizer = { field: colDef.field, search: shown.current.search }
      keptOrganizers.set(grid, organizer)
      // After React has drawn the remounted header.
      queueMicrotask(() => {
        if (keptOrganizers.get(grid) !== organizer) return
        if (!grid.getColumnHeaderElement(organizer.field)) {
          keptOrganizers.delete(grid)
          return
        }
        flushSync(() => grid.hideColumnMenu())
        grid.showColumnMenu(organizer.field)
      })
    }
  }, [apiRef, colDef.field, grid])

  // The reopened column menu grows in. Open the organizer once the menu's
  // Grow has entered (its transform is then 'none'), so Popper places it
  // against the menu's final box.
  useEffect(() => {
    const paper = anchorRef.current?.closest<HTMLElement>('.MuiPaper-root')
    if (!kept || !paper) return
    const openWhenGrown = () => {
      const grown = paper.style.transform === 'none'
      if (grown) setOpen(true)
      return grown
    }
    if (openWhenGrown()) return
    const observer = new MutationObserver(() => {
      if (openWhenGrown()) observer.disconnect()
    })
    observer.observe(paper, { attributes: true, attributeFilter: ['style'] })
    return () => observer.disconnect()
  }, [kept])
  const [placement, setPlacement] = useState<'right-start' | 'left-start'>(
    'right-start'
  )

  const location = useLocation()
  const storageKey = useMemo(
    () => getColumnStorageKey(location.pathname),
    [location.pathname]
  )

  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  // Dark mode colors
  const colors = useMemo(
    () => ({
      bg: isDarkMode ? '#162645' : '#F8FAFD',
      itemBg: isDarkMode ? '#0D192F' : '#F8FAFD',
      hoverBg: isDarkMode ? '#1D325A' : '#EFF2F7',
      text: isDarkMode ? '#FFFFFF' : '#003F61',
      checkboxBorder: isDarkMode ? '#445876' : '#E4E7EB',
      dragIcon: isDarkMode ? '#9CA3AF' : '#6B7280',
      scrollTrack: isDarkMode ? '#1D325A' : '#E4E7EB',
      scrollThumb: isDarkMode ? '#374151' : '#C1C7CD',
      scrollThumbHover: isDarkMode ? '#4B5563' : '#A0A6AD',
      linkColor: isDarkMode ? '#FFFFFF' : '#003F61',
      linkHover: isDarkMode ? '#1D325A' : '#EFF2F7',
    }),
    [isDarkMode]
  )

  // handle popper placement based on available space
  useEffect(() => {
    if (!anchorRef.current) return
    const handlePlacement = () => {
      const anchorRect = anchorRef.current!.getBoundingClientRect()
      const gridRoot = apiRef.current.rootElementRef?.current
      if (!gridRoot) return
      const gridRect = gridRoot.getBoundingClientRect()
      setPlacement(
        anchorRect.right + 300 > gridRect.right ? 'left-start' : 'right-start'
      )
    }
    handlePlacement()
    window.addEventListener('resize', handlePlacement)
    return () => window.removeEventListener('resize', handlePlacement)
  }, [anchorRef, open, apiRef])

  // fetch all columns, respecting saved localStorage settings
  useEffect(() => {
    const saved = getColumnSettings(storageKey)
    const visibleCols = apiRef.current.getVisibleColumns().map(col => col.field)
    // The grid's own columns, like the row selection checkboxes, have __
    // fields. Button columns, like the trees edit pencil, have an empty
    // header. Neither is a table column for the operator to arrange.
    const allCols = apiRef.current
      .getAllColumns()
      .filter(col => !col.field.startsWith('__') && col.headerName !== '')
      .map(col => ({
        field: col.field,
        headerName: col.headerName || col.field,
        hide: !visibleCols.includes(col.field),
      }))

    // Locked fields are always visible
    allCols.forEach(col => {
      if (LOCKED_FIELDS.has(col.field)) {
        col.hide = false
      }
    })

    if (saved) {
      // Apply saved visibility to local state
      if (saved.visibility) {
        allCols.forEach(col => {
          if (LOCKED_FIELDS.has(col.field)) return
          if (saved.visibility[col.field] !== undefined) {
            col.hide = !saved.visibility[col.field]
          }
        })
      }
      // Apply saved order to local state
      if (saved.order) {
        const orderMap = new Map(
          saved.order.map((field: string, i: number) => [field, i])
        )
        allCols.sort((a, b) => {
          const aIdx = orderMap.get(a.field)
          const bIdx = orderMap.get(b.field)
          if (aIdx === undefined && bIdx === undefined) return 0
          if (aIdx === undefined) return 1
          if (bIdx === undefined) return -1
          return aIdx - bIdx
        })
      }
    }

    setColumns(allCols)
  }, [apiRef, storageKey])

  // A visibility or order change can move the column menu. React updates
  // the menu's Popper after this organizer's, so the organizer keeps a slide
  // made for the menu's old place, and on a phone it runs off the screen.
  // Popper's update() is async, so it places the organizer again after the
  // menu has moved.
  const popper = useRef<{ update: () => unknown } | null>(null)
  useEffect(() => {
    popper.current?.update()
  }, [columns])

  // Use a ref to access latest columns without adding it as a callback dependency
  const columnsRef = useRef(columns)
  columnsRef.current = columns

  const toggleColumn = useCallback(
    (field: string) => {
      if (LOCKED_FIELDS.has(field)) return

      const isVisible = apiRef.current
        .getVisibleColumns()
        .map(c => c.field)
        .includes(field)
      apiRef.current.setColumnVisibility(field, !isVisible)
      setColumns(prev =>
        prev.map(c => (c.field === field ? { ...c, hide: !c.hide } : c))
      )

      // Persist to localStorage
      const updated = columnsRef.current.map(c =>
        c.field === field ? { ...c, hide: !c.hide } : c
      )
      saveColumnSettings(storageKey, {
        order: updated.map(c => c.field),
        visibility: Object.fromEntries(updated.map(c => [c.field, !c.hide])),
      })
    },
    [apiRef, storageKey]
  )

  // Filtered columns based on search (memoized for performance)
  const filteredColumns = useMemo(() => {
    if (!searchValue.trim()) return columns
    return columns.filter(c =>
      c.headerName.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [columns, searchValue])

  const setListedColumnsVisible = useCallback(
    (visible: boolean) => {
      // This menu lives in colDef's header. Hiding that column closes the
      // menu, and on a grid with no locked column it leaves no header to
      // reopen the organizer from.
      const fields = new Set(
        filteredColumns
          .map(c => c.field)
          .filter(
            field =>
              !LOCKED_FIELDS.has(field) && (visible || field !== colDef.field)
          )
      )
      const model = { ...gridColumnVisibilityModelSelector(apiRef) }
      fields.forEach(field => {
        model[field] = visible
      })
      apiRef.current.setColumnVisibilityModel(model)
      setColumns(prev =>
        prev.map(c => (fields.has(c.field) ? { ...c, hide: !visible } : c))
      )
    },
    [apiRef, colDef.field, filteredColumns]
  )

  const sensors = useSensors(useSensor(PointerSensor))

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (
        over &&
        active.id !== over.id &&
        !LOCKED_FIELDS.has(String(active.id)) &&
        !LOCKED_FIELDS.has(String(over.id))
      ) {
        const activeField = String(active.id)
        const overField = String(over.id)
        const oldIndex = columns.findIndex(col => col.field === activeField)
        const newIndex = columns.findIndex(col => col.field === overField)

        const updated = arrayMove(columns, oldIndex, newIndex)

        setColumns(updated)
        // The grid also counts its own __ columns, so its indexes differ.
        apiRef.current.setColumnIndex(
          activeField,
          apiRef.current.getColumnIndex(overField, false)
        )

        // Persist to localStorage
        saveColumnSettings(storageKey, {
          order: updated.map(c => c.field),
          visibility: Object.fromEntries(updated.map(c => [c.field, !c.hide])),
        })
      }
    },
    [columns, apiRef, storageKey]
  )

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={() => setOpen(prev => !prev)}
        sx={{
          color: colors.linkColor,
          cursor: 'pointer',
          justifyContent: 'flex-end',
          fontWeight: 300,
          fontStyle: 'light',
          fontSize: '14px',
          lineHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          margin: '10px',
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: colors.linkHover,
          },
        }}
      >
        ארגון עמודות <DragIndicatorIcon sx={{ ml: 1 }} />
      </Box>

      {/* Inside the column menu, so the organizer moves when the menu does. */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={placement}
        modifiers={POPPER_MODIFIERS}
        popperRef={instance => {
          popper.current = instance
        }}
        disablePortal
        style={{ zIndex: 1300 }}
      >
        <Box
          sx={{
            p: 2,
            background: colors.bg,
            borderRadius: 2,
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flexShrink: 0, mb: 1 }}>
            <SearchBar
              defaultValue={searchValue}
              onSearch={setSearchValue}
              width={200}
              height={40}
              placeholder="חיפוש עמודות..."
            />
            <Box dir="rtl" sx={{ display: 'flex', gap: 1, mt: 1, width: 200 }}>
              {BULK_VISIBILITY_ACTIONS.map(({ label, visible }) => (
                <ButtonBase
                  key={label}
                  disableRipple
                  onClick={() => setListedColumnsVisible(visible)}
                  sx={{
                    color: colors.linkColor,
                    fontFamily: 'inherit',
                    fontWeight: 300,
                    fontSize: '14px',
                    lineHeight: '100%',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease',
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: colors.linkHover,
                    },
                  }}
                >
                  {label}
                </ButtonBase>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              minHeight: 0,
              maxHeight: LIST_MAX_HEIGHT,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: colors.scrollTrack,
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: colors.scrollThumb,
                borderRadius: '4px',
                transition: 'background 0.3s ease',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: colors.scrollThumbHover,
              },
              scrollbarWidth: 'thin',
              scrollbarColor: `${colors.scrollThumb} ${colors.scrollTrack}`,
              scrollBehavior: 'smooth',
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={filteredColumns.map(c => c.field)}>
                {filteredColumns.map(col => (
                  <SortableItem
                    key={col.field}
                    id={col.field}
                    col={col}
                    toggleColumn={toggleColumn}
                    colors={colors}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Box>
        </Box>
      </Popper>
    </>
  )
}
